import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, 
  LogOut,
  Users,
  AlertTriangle,
  Settings,
  Search,
  Loader2,
  LayoutDashboard,
  Briefcase,
  CheckCircle,
  XCircle,
  Clock,
  Eye
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import { AGENT_CODES } from '../config/environment';
import type { Agent, ExceptionRequest } from '../types';

interface RequestsPageProps {
  user: Agent;
  onLogout: () => void;
}

export default function RequestsPage({ user, onLogout }: RequestsPageProps) {
  const [requests, setRequests] = useState<ExceptionRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<ExceptionRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedRequest, setSelectedRequest] = useState<ExceptionRequest | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState<string | null>(null);

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [requests, searchTerm, statusFilter, typeFilter]);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const result = await serenityClient.queryData<ExceptionRequest>(
        'SELECT * FROM ExceptionRequests ORDER BY created_at DESC'
      );
      setRequests(result);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.case_id.toLowerCase().includes(term) ||
        r.reason.toLowerCase().includes(term) ||
        r.description.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(r => r.request_type === typeFilter);
    }

    setFilteredRequests(filtered);
  };

  const getAIRecommendation = async (request: ExceptionRequest) => {
    setIsProcessing(true);
    setAiRecommendation(null);
    try {
      const result = await serenityClient.executeAgentWithContext(AGENT_CODES.EVALUADOR, {
        caseId: request.case_id,
        debtorId: request.debtor_id,
        additionalData: {
          requestType: request.request_type,
          reason: request.reason,
          description: request.description,
          proposedAmount: request.proposed_amount,
        },
      });
      setAiRecommendation(result.response);
    } catch (error) {
      console.error('Failed to get AI recommendation:', error);
      setAiRecommendation('Failed to generate recommendation. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const updateRequestStatus = async (requestId: string, status: 'approved' | 'denied', notes: string) => {
    try {
      // In a real implementation, this would update the database
      setRequests(prev => prev.map(r => 
        r.request_id === requestId 
          ? { ...r, status, decision_notes: notes, decided_by: user.agent_id, decided_at: new Date().toISOString() }
          : r
      ));
      setSelectedRequest(null);
      setAiRecommendation(null);
    } catch (error) {
      console.error('Failed to update request:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'denied': return 'bg-red-100 text-red-800';
      case 'pending': case 'under_review': return 'bg-yellow-100 text-yellow-800';
      case 'escalated': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'denied': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getStatusLabel = (status: string) => status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/cases', icon: Briefcase, label: 'Cases' },
    { path: '/debtors', icon: Users, label: 'Debtors' },
    { path: '/requests', icon: AlertTriangle, label: 'Requests', active: true },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col">
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-semibold">DFS Collections</h1>
              <p className="text-xs text-slate-400">Agent Portal</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    item.active ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-sm font-medium">
                {user.first_name[0]}{user.last_name[0]}
              </div>
              <div>
                <p className="text-sm font-medium">{user.first_name}</p>
                <p className="text-xs text-slate-400">{user.role}</p>
              </div>
            </div>
            <button onClick={onLogout} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Exception Requests</h1>
            <p className="text-gray-500">Review and process hardship and exception requests</p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search requests..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
                <option value="escalated">Escalated</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                <option value="hardship">Hardship</option>
                <option value="dispute">Dispute</option>
                <option value="settlement">Settlement</option>
                <option value="payment_holiday">Payment Holiday</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Requests List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {isLoading ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No requests found matching your criteria.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <div key={request.request_id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {getStatusIcon(request.status)}
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Link to={`/case/${request.case_id}`} className="font-semibold text-gray-900 hover:text-blue-600">
                              {request.case_id}
                            </Link>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(request.status)}`}>
                              {getStatusLabel(request.status)}
                            </span>
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                              {request.request_type}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mb-1"><strong>Reason:</strong> {request.reason}</p>
                          <p className="text-sm text-gray-600 mb-2">{request.description.substring(0, 150)}...</p>
                          {request.proposed_amount && (
                            <p className="text-sm text-gray-600">
                              <strong>Proposed amount:</strong> {formatCurrency(request.proposed_amount)}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-2">{formatDate(request.created_at)}</p>
                        </div>
                      </div>
                      
                      {request.status === 'pending' || request.status === 'under_review' ? (
                        <button
                          onClick={() => { setSelectedRequest(request); setAiRecommendation(null); }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          Review
                        </button>
                      ) : request.decision_notes && (
                        <div className="text-right max-w-xs">
                          <p className="text-xs text-gray-500">Decision notes:</p>
                          <p className="text-sm text-gray-700">{request.decision_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 text-sm text-gray-500">
            Showing {filteredRequests.length} of {requests.length} requests
          </div>
        </div>
      </main>

      {/* Review Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Review Request</h2>
                <button onClick={() => { setSelectedRequest(null); setAiRecommendation(null); }} className="text-gray-400 hover:text-gray-600">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">Case ID</p><p className="font-medium">{selectedRequest.case_id}</p></div>
                <div><p className="text-sm text-gray-500">Request Type</p><p className="font-medium">{selectedRequest.request_type}</p></div>
                <div><p className="text-sm text-gray-500">Reason</p><p className="font-medium">{selectedRequest.reason}</p></div>
                <div><p className="text-sm text-gray-500">Requested Action</p><p className="font-medium">{selectedRequest.requested_action}</p></div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Description</p>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{selectedRequest.description}</p>
              </div>
              
              {selectedRequest.proposed_amount && (
                <div>
                  <p className="text-sm text-gray-500">Proposed Amount</p>
                  <p className="font-medium text-lg">{formatCurrency(selectedRequest.proposed_amount)}</p>
                </div>
              )}

              {/* AI Recommendation */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">AI Recommendation</h3>
                  <button
                    onClick={() => getAIRecommendation(selectedRequest)}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                    {isProcessing ? 'Analyzing...' : 'Get AI Analysis'}
                  </button>
                </div>
                {aiRecommendation && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <pre className="text-sm text-purple-800 whitespace-pre-wrap">{aiRecommendation}</pre>
                  </div>
                )}
              </div>

              {/* Decision Buttons */}
              <div className="flex gap-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => updateRequestStatus(selectedRequest.request_id, 'approved', 'Request approved based on review.')}
                  className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Approve
                </button>
                <button
                  onClick={() => updateRequestStatus(selectedRequest.request_id, 'denied', 'Request denied based on review.')}
                  className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                >
                  <XCircle className="w-5 h-5" />
                  Deny
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
